import React, { createContext, useContext, useState } from 'react'
import CompaignModal, { CompaignFormValues } from './CompaignModal'
import ConfirmationPopup from 'components/CommonComponents/ConfirmationPopup'
import useLocaleText from 'components/useLocaleText'

interface CompaignModalsContextType {
  openCompaignModal: (opts?: OpenCompaignModalOpts) => void
  requestCloseCompaignModal: () => void
}

interface OpenCompaignModalOpts {
  onCreated?: (data: CompaignFormValues) => void
  initialData?: CompaignFormValues | null
}

const CompaignModalsContext = createContext<CompaignModalsContextType | null>(null)

export const useCompaignModals = () => {
  const ctx = useContext(CompaignModalsContext)
  if (!ctx) throw new Error('useCompaignModals must be used within CompaignModalsProvider')
  return ctx
}

export const CompaignModalsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const admin_cancel_confirm = useLocaleText('admin_cancel_confirm')
  const admin_cancel_desc = useLocaleText('card_confirm_desc')
  const admin_no_cancel = useLocaleText('admin_no_cancel')
  const admin_yes_cancel = useLocaleText('admin_yes_cancel_button')

  const [onCompaignCreated, setOnCompaignCreated] = useState<((data: CompaignFormValues) => void) | null>(null)
  const [compaignModalOpen, setCompaignModalOpen] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [initialData, setInitialData] = useState<CompaignFormValues | null>(null)

  const openCompaignModal = (opts?: OpenCompaignModalOpts) => {
    setOnCompaignCreated(() => opts?.onCreated || null)
    setInitialData(opts?.initialData || null)
    setCompaignModalOpen(true)
  }

  const requestCloseCompaignModal = () => {
    setConfirmationOpen(true)
  }

  const closeCompaignModal = () => {
    setCompaignModalOpen(false)
    setOnCompaignCreated(null)
    setInitialData(null)
  }

  const confirmClose = () => {
    closeCompaignModal()
    setConfirmationOpen(false)
  }

  const cancelClose = () => setConfirmationOpen(false)

  return (
    <CompaignModalsContext.Provider value={{ openCompaignModal, requestCloseCompaignModal }}>
      {children}

      {compaignModalOpen && (
        <CompaignModal
          isOpen={compaignModalOpen}
          onClose={closeCompaignModal}
          onRequestClose={requestCloseCompaignModal}
          onCreated={onCompaignCreated || (() => {})}
          campaign={initialData}
        />
      )}

      {confirmationOpen && (
        <ConfirmationPopup
          isOpen
          onCancel={cancelClose}
          onConfirm={confirmClose}
          isLoading={false}
          title={admin_cancel_confirm}
          description={admin_cancel_desc}
          cancelButtonText={admin_no_cancel}
          confirmButtonText={admin_yes_cancel}
        />
      )}
    </CompaignModalsContext.Provider>
  )
}
